import styled from 'helpers/styles';
import {css} from 'styled-components';
import PropTypes from 'prop-types';
import ComponentStyles from 'styles/mixins/Component.styles';
import Box from 'components/atoms/Layout/Box/Box';
import utils from 'helpers/utils';

const StyledTableWrapper = styled(Box)
  .attrs((props) => {
    let attr = props;

    const vars = {
      '--table-wrapper-bounds-top': props.$wrapper?.boundsCum?.top ?? '0px',
      '--table-wrapper-bounds-right': props.$wrapper?.boundsCum?.right ?? '0px',
      '--table-wrapper-bounds-bottom': props.$wrapper?.boundsCum?.bottom ?? '0px',
      '--table-wrapper-bounds-left': props.$wrapper?.boundsCum?.left ?? '0px',
      '--table-wrapper-table-height': props.$tableHeight ?? '0px',
      '--table-wrapper-header-height': props.$headerHeight ?? '0px',
      '--table-wrapper-border-left': props.$borders?.left,
      '--table-wrapper-border-right': props.$borders?.right,
      '--table-wrapper-border-bottom': props.$borders?.bottom
    };

    return utils.styleProps(attr, vars);
  })`
  &.TableWrapper {
    &-sticky {
      .TableWrapper-header {
        top: ${props => props.$css.$var('--table-wrapper-bounds-top')};
      }
    }
  }
  
  .TableWrapper {
    &-header {
      position: sticky;
      overflow: hidden;
      scroll-margin: 0;
      left: ${props => props.$css.$var('--table-wrapper-bounds-left')};
      width: calc(100vw - (${props => props.$css.$var('--table-wrapper-bounds-left')} + ${props => props.$css.$var('--table-wrapper-bounds-right')}));

      z-index: ${props => props.theme.property('zIndex.header')};
      background-color: ${props => props.theme.property('palette.background.paper')};
    }
  }
  
  .TableWrapper-content {
    display: flex;
    
    .Table {
      height: fit-content;
      
      .MuiTableHead-root {
        top: calc(${props => props.$css.$var('--table-wrapper-bounds-top')} - 0.5px);
      }
      
      ${props => !props.$boxed ? css`
        padding: 0;
        margin: 0;
      ` : null}
    }
  
    .TableWrapper {
      &-left, &-right, &-bottom {
        position: sticky;
        background-color: ${props => props.theme.property('palette.background.default')};
        z-index: ${props => props.theme.property('zIndex.context')};
      }

      &-bottom {
        position: fixed;
      }
      
      &-left, &-right {
        top: ${props => props.$css.$var('--table-wrapper-bounds-top')};
      }

      &-left, &-right, &-bottom {
        bottom: ${props => props.$css.$var('--table-wrapper-bounds-bottom')};
      }

      &-left, &-bottom {
        left: ${props => props.$css.$var('--table-wrapper-bounds-left')};
      }

      &-right, &-bottom {
        right: ${props => props.$css.$var('--table-wrapper-bounds-right')};
      }

      &-left, &-right {
        height: calc(100vh - (${props => props.$css.$var('--table-wrapper-bounds-top')} + ${props => props.$css.$var('--table-wrapper-bounds-bottom')}));
      }
      
      &-left {
        width: ${props => props.$css.$var('--table-wrapper-border-left', false)};
      }

      &-right {
        width: ${props => props.$css.$var('--table-wrapper-border-right', false)};

        .ScrollSlider {
          position: absolute;
          right: ${props => props.theme.layout(-2)};
          top: 50%;
          transform: translateY(-50%);
          height: min(${props => props.theme.layout(480)}, calc(100% - (2 * ${props => props.theme.layout('3sp')})));
        }
      }
      
      &-bottom {
        height: ${props => props.$css.$var('--table-wrapper-border-bottom', false)};
        width: calc(100vw - (${props => props.$css.$var('--table-wrapper-bounds-left')} + ${props => props.$css.$var('--table-wrapper-bounds-right')}));

        .ScrollSlider {
          position: absolute;
          bottom: ${props => props.theme.layout(-2)};
          left: 50%;
          transform: translateX(-50%);
          width: min(${props => props.theme.layout(480)}, calc(100% - (2 * ${props => props.theme.layout('3sp')})));
        }
      }
    }
  }

  &.TableWrapper-dividers {
    .TableWrapper {
      ${props => props.$boxed ? css`
        &-left {
          border-right: 1px solid ${props => props.theme.property('palette.divider')};
        }

        &-right {
          border-left: 1px solid ${props => props.theme.property('palette.divider')};
        }

        &-bottom {
          height: max(${props.$css.$var('--table-wrapper-border-bottom', false)}, calc(
            (
             100vh - 
             (${props => props.$css.$var('--table-wrapper-table-height')} + ${props => props.$css.$var('--table-wrapper-header-height')} + ${props => props.$css.$var('--table-wrapper-bounds-top')} + ${props => props.$css.$var('--table-wrapper-bounds-bottom')})
             + 1px)
          ));
          
          &::before {
            display: block;
            border-top: 1px solid ${props => props.theme.property('palette.divider')};
            width: calc(100% - ${props.$css.$var('--table-wrapper-border-left', false)} - ${props.$css.$var('--table-wrapper-border-right', false)} + 2px);
            margin-left: calc(${props.$css.$var('--table-wrapper-border-left', false)} - 1px);
            content: '';
          }
        }

        &-content {
          .Table {
            margin-bottom: 0;
            
            .MuiTableHead-root {
              tr {
                &:first-child {
                  .TableHeader {
                    border-top: 1px solid ${props => props.theme.property('palette.divider')};
                  }
                }
              }
            }
          }
        }
      ` : null}
    }
  }

  ${props => props.theme.breakpoints.down('xl')} {
    .TableWrapper-content {
      .TableWrapper {
        &-right {
          .ScrollSlider {
            height: min(${props => props.theme.layout(400)}, calc(100% - (2 * ${props => props.theme.layout('3sp')})));
          }
        }

        &-bottom {
          .ScrollSlider {
            width: min(${props => props.theme.layout(400)}, calc(100% - (2 * ${props => props.theme.layout('3sp')})));
          }
        }
      }
    }
  }
  
  ${props => props.theme.breakpoints.down('lg')} {
    .TableWrapper-content {
      .TableWrapper {
        &-right {
          .ScrollSlider {
            height: min(${props => props.theme.layout(320)}, calc(100% - (2 * ${props => props.theme.layout('2sp')})));
          }
        }

        &-bottom {
          .ScrollSlider {
            width: min(${props => props.theme.layout(320)}, calc(100% - (2 * ${props => props.theme.layout('2sp')})));
          }
        }
      }
    }
  }
  
  ${ComponentStyles}
`;

StyledTableWrapper.propTypes = {
  theme: PropTypes.object,
  $borders: PropTypes.object
};

export default StyledTableWrapper;
